import { Component, ChangeDetectorRef } from '@angular/core';
import { FormGroup, FormControl, Validators, FormBuilder } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { firstValueFrom } from 'rxjs';
import { ActivatedRoute, Router } from '@angular/router';
import { TransactionResultComponent } from '../transaction-result/transaction-result.component';
import { COMMA, ENTER } from '@angular/cdk/keycodes';
import { MatChipInputEvent } from '@angular/material/chips';

// Interfaces for the new policy structure
interface Condition {
  subject: 'Sender' | 'Receiver';
  name: string;
  predicate: string;
  value: string | number | string[];
  issuers?: string[];
}

interface ConditionGroup {
  description?: string;
  composition: 'ALL' | 'ANY';
  conditions: Condition[];
}

interface Policy {
  name?: string;
  description?: string;
  trustedIssuers: string[];
  permanent: boolean;
  groupComposition: 'ALL' | 'ANY';
  groups: ConditionGroup[];
}

interface NewConditionInput {
  subject: 'Sender' | 'Receiver' | null;
  name: string;
  predicate: string;
  value: string;
  valueString: string; // For array predicates (in, not_in)
  issuersString: string;
}

@Component({
  selector: 'ssi-assets-transfer-initiate',
  templateUrl: './assets-transfer-initiate.component.html',
  styleUrls: ['./assets-transfer-initiate.component.scss'],
})
export class AssetsTransferInitiateComponent {
  form: FormGroup;
  id: string;
  isZeroToken: boolean;

  // New policy structure
  policies: Policy[] = [];

  // For chip input
  separatorKeysCodes: number[] = [ENTER, COMMA];

  // Track new condition inputs for each policy/group combination
  newCondition: NewConditionInput[][] = [];

  conditionDisplayedColumns: string[] = ['subject', 'name', 'predicate', 'value', 'issuers', 'actions'];

  predicates = [
    { view: 'greater than', value: '>' },
    { view: 'greater than or equal to', value: '>=' },
    { view: 'less than', value: '<' },
    { view: 'less than or equal to', value: '<=' },
    { view: 'equal to', value: '=' },
    { view: 'not equal to', value: '!=' },
    { view: 'member of', value: 'in' },
    { view: 'not member of', value: 'not_in' },
  ];

  subjects: ('Sender' | 'Receiver')[] = ['Sender', 'Receiver'];

  constructor(
    private route: ActivatedRoute,
    private dialog: MatDialog,
    private router: Router,
    private fb: FormBuilder,
    private cdRef: ChangeDetectorRef
  ) {
    this.id = this.route.snapshot.paramMap.get('id') as string;
    this.isZeroToken = this.id === '0x0000000000000000000000000000000000000000000000000000000000000000';

    this.form = this.fb.group({
      to: new FormControl('', Validators.required),
      amount: new FormControl('', Validators.required),
    });
  }

  // ==================== Policy Layer Methods ====================

  addPolicyLayer(): void {
    const newPolicy: Policy = {
      name: '',
      description: '',
      trustedIssuers: [],
      permanent: false,
      groupComposition: 'ALL',
      groups: [],
    };
    this.policies.push(newPolicy);
    this.newCondition.push([]); // Initialize newCondition array for this policy
    this.cdRef.detectChanges();
  }

  removePolicyLayer(policyIndex: number): void {
    if (policyIndex >= 0 && policyIndex < this.policies.length) {
      this.policies.splice(policyIndex, 1);
      this.newCondition.splice(policyIndex, 1);
      this.cdRef.detectChanges();
    }
  }

  // ==================== Trusted Issuers Methods ====================

  addIssuer(policyIndex: number, event: MatChipInputEvent): void {
    const value = (event.value || '').trim();
    if (value) {
      this.policies[policyIndex].trustedIssuers.push(value);
    }
    event.chipInput!.clear();
    this.cdRef.detectChanges();
  }

  removeIssuer(policyIndex: number, issuerIndex: number): void {
    this.policies[policyIndex].trustedIssuers.splice(issuerIndex, 1);
    this.cdRef.detectChanges();
  }

  // ==================== Condition Group Methods ====================

  addConditionGroup(policyIndex: number): void {
    const newGroup: ConditionGroup = {
      description: '',
      composition: 'ALL',
      conditions: [],
    };
    this.policies[policyIndex].groups.push(newGroup);

    // Initialize new condition input for this group
    this.newCondition[policyIndex].push(this.createEmptyConditionInput());
    this.cdRef.detectChanges();
  }

  removeConditionGroup(policyIndex: number, groupIndex: number): void {
    if (groupIndex >= 0 && groupIndex < this.policies[policyIndex].groups.length) {
      this.policies[policyIndex].groups.splice(groupIndex, 1);
      this.newCondition[policyIndex].splice(groupIndex, 1);
      this.cdRef.detectChanges();
    }
  }

  // ==================== Condition Methods ====================

  createEmptyConditionInput(): NewConditionInput {
    return {
      subject: null,
      name: '',
      predicate: '',
      value: '',
      valueString: '',
      issuersString: '',
    };
  }

  isArrayPredicate(predicate: string): boolean {
    return predicate === 'in' || predicate === 'not_in';
  }

  canAddCondition(policyIndex: number, groupIndex: number): boolean {
    const input = this.newCondition[policyIndex]?.[groupIndex];
    if (!input) return false;

    const hasBasicFields = input.subject && input.name && input.predicate;

    if (this.isArrayPredicate(input.predicate)) {
      return <boolean><unknown>hasBasicFields && input.valueString.trim().length > 0;
    }
    return <boolean><unknown>hasBasicFields && input.value.toString().trim().length > 0;
  }

  addCondition(policyIndex: number, groupIndex: number): void {
    const input = this.newCondition[policyIndex][groupIndex];

    let value: string | number | string[];
    if (this.isArrayPredicate(input.predicate)) {
      // Parse comma-separated values into array
      value = input.valueString.split(',').map(v => v.trim()).filter(v => v.length > 0);
    } else {
      // Try to parse as number, otherwise keep as string
      const numValue = parseFloat(input.value);
      value = isNaN(numValue) ? input.value : numValue;
    }

    const newCondition: Condition = {
      subject: input.subject as 'Sender' | 'Receiver',
      name: input.name,
      predicate: input.predicate,
      value: value,
    };

    // Add issuers if specified
    if (input.issuersString.trim().length > 0) {
      newCondition.issuers = input.issuersString.split(',').map(v => v.trim()).filter(v => v.length > 0);
    }

    this.policies[policyIndex].groups[groupIndex].conditions.push(newCondition);

    // Reset the input form
    this.newCondition[policyIndex][groupIndex] = this.createEmptyConditionInput();

    // Trigger table refresh
    this.policies[policyIndex].groups[groupIndex].conditions =
      [...this.policies[policyIndex].groups[groupIndex].conditions];

    this.cdRef.detectChanges();
  }

  removeCondition(policyIndex: number, groupIndex: number, conditionIndex: number): void {
    const conditions = this.policies[policyIndex].groups[groupIndex].conditions;
    if (conditionIndex >= 0 && conditionIndex < conditions.length) {
      conditions.splice(conditionIndex, 1);
      // Trigger table refresh
      this.policies[policyIndex].groups[groupIndex].conditions = [...conditions];
      this.cdRef.detectChanges();
    }
  }

  // ==================== Display Helpers ====================

  getPredicateView(predicateValue: string): string {
    const pred = this.predicates.find(p => p.value === predicateValue);
    return pred ? pred.view : predicateValue;
  }

  formatValue(value: string | number | string[]): string {
    if (Array.isArray(value)) {
      return `[${value.join(', ')}]`;
    }
    return String(value);
  }

  // ==================== Form Submission ====================

  async initiate(): Promise<void> {
    // Validate policies
    if (!this.validatePolicies()) {
      return;
    }

    const closed = await firstValueFrom(
      this.dialog
        .open(TransactionResultComponent, {
          data: {
            to: this.form.get('to')?.value,
            id: this.id,
            amount: this.form.get('amount')?.value,
            policies: this.policies, // Pass the new policy structure
          },
          disableClose: true,
        })
        .afterClosed()
    );

    if (closed) {
      await this.router.navigate(['/assets']);
    }
  }

  validatePolicies(): boolean {
    // Check permanent is only on last layer
    for (let i = 0; i < this.policies.length - 1; i++) {
      if (this.policies[i].permanent) {
        alert(`Policy Layer ${i + 1} cannot be permanent. Only the last layer can be permanent.`);
        return false;
      }
    }

    // Check each policy has at least one group with at least one condition
    for (let i = 0; i < this.policies.length; i++) {
      if (this.policies[i].groups.length === 0) {
        alert(`Policy Layer ${i + 1} must have at least one condition group.`);
        return false;
      }

      for (let j = 0; j < this.policies[i].groups.length; j++) {
        if (this.policies[i].groups[j].conditions.length === 0) {
          alert(`Policy Layer ${i + 1}, Group ${j + 1} must have at least one condition.`);
          return false;
        }
      }
    }

    return true;
  }

  cancel(): void {
    this.router.navigate(['/assets']);
  }
}
